import { SHARED_IGNORE_ERRORS } from './ignore-errors.js';

export { SHARED_IGNORE_ERRORS };

/** Stages where we never want to ship events to Sentry. */
const NON_REPORTING_STAGES = ['development', 'dev', 'test', 'testing', 'localstaging'];

/**
 * Builds the Sentry.init options every NearSt frontend should share.
 *
 * Pure — it does no I/O and touches no SDK — so the defaults can be asserted in
 * a unit test rather than discovered in production. `initSentry` is the thin
 * wrapper that actually calls the SDK.
 *
 * @param {object} options
 * @param {string} [options.dsn]                Project DSN. A falsy DSN disables reporting rather
 *                                              than throwing — shopping-service supplies it from an
 *                                              env var, and an environment without it must degrade
 *                                              to "no telemetry", never crash an entry bundle that
 *                                              inits at module scope.
 * @param {string} options.service              Frontend name, attached as a `service` tag.
 * @param {string} options.stage                Deploy stage — becomes `environment`.
 * @param {string} [options.release]            Release identity. MUST match the name the deploy
 *                                              workflow uploads sourcemaps under, or traces stay
 *                                              minified. Falsy values are omitted rather than sent
 *                                              as undefined/"".
 * @param {boolean} [options.enabled]           Override the stage-derived on/off decision.
 * @param {number} [options.tracesSampleRate]   Performance sampling. Defaults to 0 (off) — tracing
 *                                              is opt-in per frontend so nobody turns it on for the
 *                                              whole estate by accident.
 * @param {boolean} [options.replayOnError]     Record a Session Replay when an error fires. Session
 *                                              sampling stays at 0 either way: full-session replay
 *                                              is the expensive one, and turning it on across the
 *                                              estate would blow the entry-tier plan.
 * @param {Array} [options.integrations]        SDK-version-specific — the caller constructs these,
 *                                              because v7 (`new Sentry.Replay()`) and v8
 *                                              (`Sentry.replayIntegration()`) disagree on the shape.
 * @param {Array} [options.ignoreErrors]        Appended to the shared list, never replaces it.
 * @param {object} [options.extra]              Escape hatch for genuinely per-app options.
 */
export function buildSentryOptions({
	dsn,
	service,
	stage,
	release,
	enabled,
	tracesSampleRate = 0,
	replayOnError = false,
	integrations,
	ignoreErrors = [],
	extra = {}
} = {}) {
	if (!service) throw new Error('sentry-config: `service` is required');
	if (!stage) throw new Error('sentry-config: `stage` is required');

	return {
		...(dsn ? { dsn } : {}),
		environment: stage,
		// Omit rather than send an empty release: Sentry treats "" as a real release
		// name and would file events under a release that has no artefacts, which is
		// harder to spot than no release at all.
		...(release ? { release } : {}),
		// A missing DSN forces reporting off regardless of the override — there is
		// nowhere to send events, and `enabled: true` with no DSN would only differ
		// by logging SDK noise.
		enabled: Boolean(dsn) && (enabled ?? !NON_REPORTING_STAGES.includes(stage)),
		initialScope: { tags: { service } },
		tracesSampleRate,
		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: replayOnError ? 1.0 : 0,
		ignoreErrors: [...SHARED_IGNORE_ERRORS, ...ignoreErrors],
		...(integrations ? { integrations } : {}),
		...extra
	};
}

/**
 * Initialises Sentry with the shared defaults.
 *
 * The SDK is passed in rather than imported. The four customer-facing frontends
 * split across two different Sentry packages (`@sentry/react` for the Vite
 * dashboards, `@sentry/nextjs` for Product Locator and Shopping), and importing
 * either one here would drag a second SDK copy into every consumer's bundle and
 * give it a second, uninitialised client.
 *
 * @param {object} Sentry  The app's own `@sentry/*` namespace import.
 * @param {object} options See `buildSentryOptions`.
 * @returns {object} The resolved options, so callers can assert on them in tests.
 */
export function initSentry(Sentry, options) {
	const resolved = buildSentryOptions(options);
	Sentry.init(resolved);
	return resolved;
}

/**
 * Attaches the signed-in user and their org/shop context to every subsequent event.
 *
 * Call this wherever the app resolves its auth state, and call it with
 * `userId: null` when signed out so a shared browser does not keep attributing
 * events to whoever logged in first.
 *
 * Only the Clerk user id is sent — never email or name. Sentry is a
 * third-party processor and the id is enough to identify the account internally.
 *
 * @param {object} Sentry
 * @param {object} context
 * @param {string|null} context.userId    Clerk user id, or null to clear.
 * @param {string} [context.organisation] Clerk org slug.
 * @param {string} [context.clerkOrganisationId]
 * @param {string} [context.shopId]
 */
export function setSentryUser(Sentry, { userId, organisation, clerkOrganisationId, shopId } = {}) {
	Sentry.setUser(userId ? { id: userId } : null);
	Sentry.setTag('organisation', organisation ?? null);
	Sentry.setTag('clerkOrganisationId', clerkOrganisationId ?? null);
	Sentry.setTag('shopId', shopId ?? null);
}
